import React from "react";

/**
 * The one card frame used by every block of the normativ editor (/os, /os/archive) and the
 * work order (/rn-work-order): Готов Производ, Податоци, Меѓу/Полупроизвод, Log Book,
 * Размерување, Материјално, Материјално прием, InProcess, Параметри за монтажа.
 *
 * Purely visual. Same radius, shadow, header height and padding everywhere, so the cards
 * line up as one system. Variants are props (rule 7b) — never restyle a card from the call
 * site with descendant selectors.
 */

/** Card frame / header / title classes — exported for the one caller that may change only
 *  classes (OSProductivityTable); everything else renders <OSCard>. */
export const OS_CARD_CLS = "bg-white rounded-lg border border-gray-200 shadow-md shadow-gray-300/40 min-w-0";
export const OS_CARD_GAP_CLS = "mb-3";
export const OS_CARD_HEADER_CLS =
  "themed-gradient min-h-[44px] px-3 py-1.5 rounded-t-lg flex items-center justify-between gap-2";
export const OS_CARD_TITLE_CLS = "text-white font-semibold text-sm leading-tight truncate";
/** Compact button on the green card header (short label; the full name goes in `title`). */
export const OS_CARD_HEADER_BUTTON_CLS =
  "inline-flex items-center rounded border border-white/40 px-2 py-0.5 text-[11px] font-semibold text-white bg-white/15 hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/15";
/** Same button while the thing it toggles is shown. */
export const OS_CARD_HEADER_BUTTON_ACTIVE_CLS = "!bg-white !text-blue-700";

/** Table header cell shared by all normativ tables — one height, size and fill. */
export const OS_TH_CLS =
  "border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] h-[26px] px-1.5 align-middle";

/** Soft read-only look for computed / codebook-derived values. */
export const OS_READONLY_CLS = "bg-gray-50 text-gray-600 cursor-not-allowed";

/** Editable cell that is disabled (archived / signed normativ — a disabled <fieldset>, or a
 *  locked actual) reads as plain text on the white cell: no grey box, no faded text.
 *  Only add it to user-entered cells — computed values keep OS_READONLY_CLS. */
export const OS_PLAIN_DISABLED_CLS =
  "disabled:bg-transparent disabled:text-gray-800 disabled:opacity-100 disabled:cursor-default";
/** Same for a <select>: also hides the dropdown arrow while disabled. */
export const OS_PLAIN_DISABLED_SELECT_CLS = `${OS_PLAIN_DISABLED_CLS} disabled:appearance-none`;

/** Calm empty-state line inside a card body. */
export const OS_EMPTY_CLS = "px-3 py-4 text-center text-xs text-gray-400";

/** Info strip under a card header (passed as `notice`). */
export const OSCardNotice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 text-[11px] px-3 py-1.5">
    {children}
  </div>
);

interface OSCardProps {
  title: React.ReactNode;
  /** Native tooltip on the title (full name when the title is shortened). */
  titleTooltip?: string;
  /** Small element right after the title (counts, status). */
  badge?: React.ReactNode;
  /** Right side of the header — toolbar, buttons. */
  actions?: React.ReactNode;
  /** Strip directly under the header (e.g. the sort-lock notice). */
  notice?: React.ReactNode;
  /** "narrow" caps the card at max-w-4xl (short codebook tables). */
  width?: "full" | "narrow";
  /** "bottom" (default) leaves the standard gap below the card; "none" when the parent spaces it. */
  spacing?: "bottom" | "none";
  children: React.ReactNode;
}

const OSCard: React.FC<OSCardProps> = ({
  title,
  titleTooltip,
  badge,
  actions,
  notice,
  width = "full",
  spacing = "bottom",
  children,
}) => {
  const outerCls = [
    OS_CARD_CLS,
    width === "narrow" ? "max-w-4xl" : "max-w-full",
    spacing === "bottom" ? OS_CARD_GAP_CLS : "",
  ].join(" ");

  return (
    <div className={outerCls}>
      <div className={OS_CARD_HEADER_CLS}>
        <div className="flex items-center gap-2 min-w-0">
          <h2
            className={OS_CARD_TITLE_CLS}
            title={titleTooltip}
          >
            {title}
          </h2>
          {badge}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
      {notice}
      {children}
    </div>
  );
};

export default OSCard;
